/**
 * Proposal visual engine (Ask #5) — a PURE renderer that turns a
 * proposal `BusinessDoc` + structured line items into a self-contained,
 * branded, A4/print-ready HTML document: cover (logo / title / date),
 * executive summary, scope of work, a real pricing table (one-time vs
 * recurring subtotals, optional discount, total), and a terms +
 * signature block.
 *
 * Everything here is deterministic and DOM-free so it is trivially
 * testable; the UI (ContractsView "Preview / Print") just opens the
 * returned string in a new window. All layout uses inline `style`
 * attributes so the document survives copy/paste and email clients —
 * the single <style> block carries only what CANNOT be inlined
 * (@page size + @media print rules).
 *
 * This complements `docs/brandDoc.ts` (which renders the free-form
 * markdown body); this module renders the STRUCTURED document. No
 * customer data is ever fabricated — every rendered figure comes from
 * the proposal record or the caller-supplied line items.
 */

import type { Account, BusinessDoc } from '../db/types';
import { C } from '../tokens';
import { WORDMARK_WHITE } from '../docs/brandDoc';

// ===========================================================================
// Model
// ===========================================================================

/** One priced row on the proposal's pricing table. */
export interface ProposalLineItem {
  /** What is being delivered, e.g. "Managed AI operations". */
  description: string;
  qty: number;
  /** Unit label, e.g. "mo", "seat", "project". */
  unit?: string;
  /** USD price per unit. */
  unitPrice: number;
  /** Billing cadence — defaults to one-time. */
  cadence?: 'one-time' | 'recurring';
  /** Optional percentage discount (0–100) applied to this line. */
  discountPct?: number;
}

export interface ProposalTotals {
  oneTimeSubtotal: number;
  recurringSubtotal: number;
  /** Total dollars taken off across all lines (0 when no discounts). */
  discountTotal: number;
  /** oneTimeSubtotal + recurringSubtotal (both already net of discount). */
  total: number;
}

/** Brand knobs — defaults are the Argo IQ identity from lib/tokens. */
export interface ProposalBrand {
  name?: string;
  tagline?: string;
  /** Accent hex, defaults to brand magenta. */
  accent?: string;
  /** Inline SVG logo markup (defaults to the Argo wordmark). */
  logoSvg?: string;
}

export interface RenderProposalOptions {
  /** The account the proposal addresses (POC feeds the signature block). */
  account?: Pick<
    Account,
    'name' | 'pocName' | 'pocTitle' | 'pocEmail' | 'ownerName'
  > | null;
  lineItems: ProposalLineItem[];
  brand?: ProposalBrand;
}

// ===========================================================================
// Money math (pure)
// ===========================================================================

const round2 = (n: number): number => Math.round(n * 100) / 100;

const clampPct = (pct: number | undefined): number =>
  Math.min(100, Math.max(0, pct ?? 0));

/** Net amount for one line: qty × unitPrice, less its discount. */
export function lineItemAmount(item: ProposalLineItem): number {
  const gross = item.qty * item.unitPrice;
  return round2(gross - (gross * clampPct(item.discountPct)) / 100);
}

/**
 * One-time vs recurring subtotals (each net of per-line discounts),
 * the total discount taken, and the grand total. Empty list → zeros.
 */
export function computeProposalTotals(
  items: ProposalLineItem[],
): ProposalTotals {
  let oneTime = 0;
  let recurring = 0;
  let discount = 0;
  for (const item of items) {
    const gross = item.qty * item.unitPrice;
    const off = (gross * clampPct(item.discountPct)) / 100;
    const net = gross - off;
    discount += off;
    if (item.cadence === 'recurring') recurring += net;
    else oneTime += net;
  }
  return {
    oneTimeSubtotal: round2(oneTime),
    recurringSubtotal: round2(recurring),
    discountTotal: round2(discount),
    total: round2(oneTime + recurring),
  };
}

/**
 * Derive line items from the document's own recorded value when the
 * caller has none — a single one-time engagement line. Returns [] when
 * the doc has no value: we NEVER invent pricing.
 */
export function defaultLineItemsForDoc(doc: BusinessDoc): ProposalLineItem[] {
  if (doc.value == null) return [];
  return [
    {
      description: `Engagement — ${doc.entityName}`,
      qty: 1,
      unit: 'engagement',
      unitPrice: doc.value,
      cadence: 'one-time',
    },
  ];
}

// ===========================================================================
// Rendering helpers
// ===========================================================================

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function usd(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(round2(n)) ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

const SANS = "'Manrope','Inter',system-ui,-apple-system,'Segoe UI',sans-serif";
const MONO = "'Geist Mono',ui-monospace,SFMono-Regular,Menlo,monospace";

const INK = C.ink950;
const PAPER = '#ffffff';
const PAPER_2 = '#f7f7fa';
const LINE = '#e6e7ee';
const BODY_TEXT = '#3c3f4c';
const MUTED = '#7a7f92';

/** mono uppercase label style used across cover meta + table headers. */
const label = (color: string) =>
  `font-family:${MONO};font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:${color};`;

function sectionTitle(accent: string, text: string): string {
  return (
    `<div style="height:3px;width:44px;border-radius:2px;background:linear-gradient(90deg,${accent},${C.magentaDeep});margin:0 0 12px;"></div>` +
    `<h2 style="margin:0 0 14px;font-size:24px;font-weight:800;letter-spacing:-0.01em;color:${INK};">${text}</h2>`
  );
}

const td = (extra = '') =>
  `padding:12px 14px;border-bottom:1px solid ${LINE};color:${BODY_TEXT};font-size:13.5px;${extra}`;
const th = (extra = '') =>
  `text-align:left;padding:10px 14px;border-bottom:2px solid ${INK};${label(MUTED)}${extra}`;
const NUM = 'text-align:right;font-variant-numeric:tabular-nums;';

function pricingRows(items: ProposalLineItem[], accent: string): string {
  const rows = items
    .map((item) => {
      const cadence = item.cadence === 'recurring' ? 'Recurring' : 'One-time';
      const discount = clampPct(item.discountPct);
      const desc =
        esc(item.description) +
        (discount > 0
          ? ` <span style="${label(C.magentaDeep)}">−${discount}%</span>`
          : '');
      return (
        '<tr>' +
        `<td style="${td()}">${desc}</td>` +
        `<td style="${td(NUM)}">${esc(String(item.qty))}</td>` +
        `<td style="${td()}">${esc(item.unit ?? '—')}</td>` +
        `<td style="${td()}">${cadence}</td>` +
        `<td style="${td(NUM)}font-weight:600;color:${INK};">${usd(lineItemAmount(item))}</td>` +
        '</tr>'
      );
    })
    .join('');

  const totals = computeProposalTotals(items);
  const subtotalRow = (name: string, amount: number, emphasis = false): string =>
    '<tr>' +
    `<td colspan="4" style="${td(`text-align:right;${emphasis ? `font-weight:800;color:${C.magentaDeep};background:rgba(255,0,255,0.06);` : `color:${MUTED};`}`)}">${name}</td>` +
    `<td style="${td(`${NUM}${emphasis ? `font-weight:800;color:${C.magentaDeep};background:rgba(255,0,255,0.06);` : `font-weight:600;color:${INK};`}`)}">${usd(amount)}</td>` +
    '</tr>';

  const empty =
    items.length === 0
      ? `<tr><td colspan="5" style="${td(`color:${MUTED};font-style:italic;`)}">No line items yet — pricing to be finalized with the client.</td></tr>`
      : '';

  return (
    `<table style="width:100%;border-collapse:collapse;margin:4px 0 0;">` +
    '<thead><tr>' +
    `<th style="${th()}">Description</th>` +
    `<th style="${th(NUM)}">Qty</th>` +
    `<th style="${th()}">Unit</th>` +
    `<th style="${th()}">Cadence</th>` +
    `<th style="${th(NUM)}">Amount</th>` +
    '</tr></thead>' +
    '<tbody>' +
    empty +
    rows +
    subtotalRow('One-time subtotal', totals.oneTimeSubtotal) +
    subtotalRow('Recurring subtotal', totals.recurringSubtotal) +
    (totals.discountTotal > 0
      ? subtotalRow('Discount applied', -totals.discountTotal)
      : '') +
    subtotalRow('Total', totals.total, true) +
    '</tbody></table>' +
    `<p style="margin:10px 0 0;${label(MUTED)}">Recurring amounts shown for the full quoted term.${accent ? '' : ''}</p>`
  );
}

function signatureColumn(party: string, signer: string, title?: string): string {
  const line = (name: string) =>
    `<div style="margin-top:26px;border-bottom:1px solid ${INK};height:22px;"></div>` +
    `<div style="margin-top:6px;${label(MUTED)}">${name}</div>`;
  return (
    `<div style="flex:1;min-width:220px;">` +
    `<div style="${label(MUTED)}">For</div>` +
    `<div style="margin-top:4px;font-size:15px;font-weight:800;color:${INK};">${esc(party)}</div>` +
    `<div style="margin-top:2px;font-size:12.5px;color:${MUTED};">${esc(signer)}${title ? ` · ${esc(title)}` : ''}</div>` +
    line('Signature') +
    line('Name') +
    line('Date') +
    '</div>'
  );
}

// ===========================================================================
// Document shell (shared chrome for both docKinds)
// ===========================================================================

/**
 * Wrap a cover + main + footer in the standalone, print-ready HTML
 * shell (A4 @page, print CSS, floating "Print / Save PDF" button).
 * Shared verbatim by the proposal and contract templates so both
 * export to PDF the same way.
 */
function docShell(args: {
  title: string;
  accent: string;
  cover: string;
  main: string;
  footer: string;
}): string {
  const { title, accent, cover, main, footer } = args;
  // The <style> block carries ONLY what cannot live in inline styles:
  // @page sizing and print-media rules (hide the print button, force
  // color fidelity, break the cover onto its own page).
  return (
    '<!DOCTYPE html>' +
    `<html lang="en"><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/>` +
    `<title>${esc(title)}</title>` +
    '<style>' +
    '@page{size:A4;margin:0;}' +
    '@media print{' +
    '.no-print{display:none !important;}' +
    'body{margin:0 !important;background:#fff !important;}' +
    '.pd-page{max-width:none !important;margin:0 !important;box-shadow:none !important;border-radius:0 !important;}' +
    '.pd-cover{page-break-after:always;-webkit-print-color-adjust:exact;print-color-adjust:exact;}' +
    'header,footer,table,section{-webkit-print-color-adjust:exact;print-color-adjust:exact;}' +
    'section,table{page-break-inside:avoid;}' +
    '}' +
    '</style></head>' +
    `<body style="margin:0;background:#101017;font-family:${SANS};color:${BODY_TEXT};-webkit-font-smoothing:antialiased;">` +
    `<button class="no-print" onclick="window.print()" style="position:fixed;top:16px;right:16px;z-index:10;padding:10px 18px;border:none;border-radius:999px;background:${accent};color:#fff;font-family:${SANS};font-size:13px;font-weight:700;cursor:pointer;box-shadow:0 8px 24px rgba(0,0,0,0.35);">Print / Save PDF</button>` +
    `<div class="pd-page" style="max-width:820px;margin:0 auto;background:${PAPER};box-shadow:0 24px 64px rgba(0,0,0,0.45);">` +
    `<div class="pd-cover">${cover}</div>` +
    `<main style="padding:56px 64px;">${main}</main>` +
    footer +
    '</div>' +
    '</body></html>'
  );
}

// ===========================================================================
// renderProposalHtml
// ===========================================================================

/**
 * Render a proposal OR contract document into a complete, standalone
 * HTML string — open it in a new window/tab and it is print-ready
 * (A4, dark cover, light body, clean page breaks). The floating
 * "Print / Save PDF" button is hidden by print CSS.
 *
 * The template branches on `doc.kind`:
 *   • 'proposal' → branded sales document (executive summary, scope of
 *     work, pricing table, terms, acceptance signatures).
 *   • 'contract' → legally-structured services agreement (parties,
 *     recitals, numbered clauses, execution block) — see
 *     {@link renderContractDocument}.
 */
export function renderProposalHtml(
  proposal: BusinessDoc,
  options: RenderProposalOptions,
): string {
  const brand = options.brand ?? {};
  const brandName = brand.name ?? 'Argo IQ';
  const accent =
    brand.accent ?? (proposal.kind === 'contract' ? C.blueElectric : C.magenta);
  const logo = brand.logoSvg ?? WORDMARK_WHITE;
  const account = options.account ?? null;
  const clientName = account?.name ?? proposal.entityName;
  const owner = proposal.ownerName ?? account?.ownerName ?? 'The team';
  const items = options.lineItems;
  const totals = computeProposalTotals(items);
  const date = fmtDate(proposal.createdAt);

  // CONTRACT → the legally-structured agreement template. Everything
  // below this branch is the proposal (sales document) template.
  if (proposal.kind === 'contract') {
    return renderContractDocument(proposal, {
      brandName,
      accent,
      logoSvg: logo,
      account,
      clientName,
      owner,
      items,
      totals,
      date,
    });
  }

  const cover =
    `<header style="background:${INK};color:#fff;padding:64px 64px 56px;border-radius:0;">` +
    `<div style="height:18px;">${logo}</div>` +
    `<p style="margin:36px 0 12px;${label(accent)}font-size:12px;">Proposal · ${esc(date)}</p>` +
    `<h1 style="margin:0 0 18px;font-size:40px;line-height:1.08;font-weight:800;letter-spacing:-0.02em;max-width:18ch;">${esc(proposal.title)}</h1>` +
    `<p style="margin:0;font-size:16px;line-height:1.55;color:rgba(255,255,255,0.72);max-width:58ch;">Prepared for ${esc(clientName)} by ${esc(brandName)}.${brand.tagline ? ` ${esc(brand.tagline)}` : ''}</p>` +
    `<div style="display:flex;flex-wrap:wrap;gap:36px;margin-top:40px;">` +
    `<div><div style="${label('rgba(255,255,255,0.4)')}">Prepared for</div><div style="margin-top:5px;font-size:14px;font-weight:600;">${esc(clientName)}</div></div>` +
    `<div><div style="${label('rgba(255,255,255,0.4)')}">Prepared by</div><div style="margin-top:5px;font-size:14px;font-weight:600;">${esc(owner)} · ${esc(brandName)}</div></div>` +
    `<div><div style="${label('rgba(255,255,255,0.4)')}">Date</div><div style="margin-top:5px;font-size:14px;font-weight:600;">${esc(date)}</div></div>` +
    (proposal.validUntil
      ? `<div><div style="${label('rgba(255,255,255,0.4)')}">Valid until</div><div style="margin-top:5px;font-size:14px;font-weight:600;">${esc(fmtDate(proposal.validUntil))}</div></div>`
      : '') +
    '</div>' +
    `<div style="display:inline-flex;align-items:baseline;gap:12px;margin-top:36px;padding:16px 24px;border:1px solid rgba(255,255,255,0.16);border-radius:14px;background:rgba(255,255,255,0.06);">` +
    `<span style="${label('rgba(255,255,255,0.5)')}">Engagement total</span>` +
    `<span style="font-size:28px;font-weight:800;color:${accent};">${usd(totals.total)}</span>` +
    '</div>' +
    '</header>';

  const execSummary =
    `<section style="margin:0 0 44px;">` +
    sectionTitle(accent, 'Executive summary') +
    `<p style="margin:0 0 12px;font-size:14.5px;line-height:1.65;color:${BODY_TEXT};max-width:70ch;">${esc(brandName)} proposes an engagement with ${esc(clientName)} covering ${items.length === 1 ? 'one workstream' : `${items.length || 'the'} workstreams`} detailed below, at a total engagement value of ${usd(totals.total)}${totals.recurringSubtotal > 0 ? ` (${usd(totals.oneTimeSubtotal)} one-time, ${usd(totals.recurringSubtotal)} recurring over the quoted term)` : ''}.</p>` +
    `<p style="margin:0;font-size:14.5px;line-height:1.65;color:${BODY_TEXT};max-width:70ch;">Scope, pricing, and terms in this document reflect the current draft and remain open to revision until countersigned by both parties.</p>` +
    '</section>';

  const scope =
    `<section style="margin:0 0 44px;">` +
    sectionTitle(accent, 'Scope of work') +
    (items.length > 0
      ? `<ul style="margin:0;padding-left:22px;max-width:70ch;">` +
        items
          .map(
            (item) =>
              `<li style="margin:0 0 10px;font-size:14.5px;line-height:1.6;color:${BODY_TEXT};"><strong style="color:${INK};">${esc(item.description)}</strong> — ${esc(String(item.qty))}${item.unit ? ` ${esc(item.unit)}` : ''}, ${item.cadence === 'recurring' ? 'recurring' : 'one-time'}.</li>`,
          )
          .join('') +
        '</ul>'
      : `<p style="margin:0;font-size:14.5px;line-height:1.6;color:${MUTED};font-style:italic;">Scope to be finalized with ${esc(clientName)}.</p>`) +
    '</section>';

  const pricing =
    `<section style="margin:0 0 44px;">` +
    sectionTitle(accent, 'Pricing') +
    pricingRows(items, accent) +
    '</section>';

  const terms =
    `<section style="margin:0 0 44px;">` +
    sectionTitle(accent, 'Terms') +
    `<ol style="margin:0;padding-left:22px;max-width:70ch;">` +
    [
      proposal.validUntil
        ? `This proposal is valid until ${esc(fmtDate(proposal.validUntil))}; pricing may be revised thereafter.`
        : 'This proposal is valid for 30 days from the date above unless extended in writing.',
      'One-time fees are invoiced on signature; recurring fees are invoiced at the start of each period.',
      'Payment terms are net 15 from invoice date unless otherwise agreed.',
      'Either party may terminate for material breach with 15 days written notice and opportunity to cure.',
      `All work product and confidential information exchanged under this engagement remains subject to the parties' confidentiality obligations.`,
    ]
      .map(
        (t) =>
          `<li style="margin:0 0 10px;font-size:13.5px;line-height:1.6;color:${BODY_TEXT};">${t}</li>`,
      )
      .join('') +
    '</ol>' +
    '</section>';

  const signatures =
    `<section style="margin:0;padding:28px;border:1px solid ${LINE};border-radius:14px;background:${PAPER_2};page-break-inside:avoid;">` +
    sectionTitle(accent, 'Acceptance &amp; signatures') +
    `<p style="margin:0 0 8px;font-size:13.5px;line-height:1.6;color:${BODY_TEXT};max-width:70ch;">Signing below indicates acceptance of the scope, pricing, and terms in this proposal.</p>` +
    `<div style="display:flex;flex-wrap:wrap;gap:40px;margin-top:8px;">` +
    signatureColumn(brandName, owner) +
    signatureColumn(clientName, account?.pocName ?? 'Authorized signatory', account?.pocTitle) +
    '</div>' +
    '</section>';

  const footer =
    `<footer style="background:${INK};color:rgba(255,255,255,0.6);padding:22px 64px;display:flex;align-items:center;justify-content:space-between;gap:20px;${label('rgba(255,255,255,0.6)')}">` +
    `<span style="height:14px;display:inline-flex;">${logo}</span>` +
    `<span>${esc(brandName)} · ${esc(proposal.title)}</span>` +
    '</footer>';

  return docShell({
    title: proposal.title,
    accent,
    cover,
    main: execSummary + scope + pricing + terms + signatures,
    footer,
  });
}

// ===========================================================================
// Contract template (docKind === 'contract')
// ===========================================================================

interface ContractRenderContext {
  brandName: string;
  accent: string;
  logoSvg: string;
  account: RenderProposalOptions['account'];
  clientName: string;
  owner: string;
  items: ProposalLineItem[];
  totals: ProposalTotals;
  /** Formatted effective date (from doc.createdAt). */
  date: string;
}

/**
 * The legally-structured agreement template: cover, PARTIES, RECITALS
 * (WHEREAS…), numbered operative clauses (scope, fees, term &
 * termination, confidentiality, IP, warranties, liability cap,
 * relationship, governing law, entire agreement), and an
 * "IN WITNESS WHEREOF" execution block with signature lines for both
 * parties. Pricing comes from the same line items as the proposal —
 * nothing is fabricated; open legal fields (e.g. governing-law state)
 * render as fill-in blanks rather than invented facts.
 */
function renderContractDocument(
  contract: BusinessDoc,
  ctx: ContractRenderContext,
): string {
  const { brandName, accent, logoSvg, account, clientName, owner, items, totals, date } =
    ctx;

  const pocName = account?.pocName ?? 'Authorized signatory';
  const pocTitle = account?.pocTitle;

  /** Body paragraph in contract voice. */
  const p = (html: string, extra = ''): string =>
    `<p style="margin:0 0 10px;font-size:13.5px;line-height:1.7;color:${BODY_TEXT};max-width:74ch;${extra}">${html}</p>`;

  /** One numbered operative clause. */
  const clause = (no: number, heading: string, bodyHtml: string): string =>
    `<section style="margin:0 0 26px;">` +
    `<h3 style="margin:0 0 8px;font-size:15px;font-weight:800;letter-spacing:-0.005em;color:${INK};"><span style="color:${accent};">${no}.</span> ${heading}</h3>` +
    bodyHtml +
    '</section>';

  const defined = (term: string): string =>
    `<strong style="color:${INK};">&quot;${term}&quot;</strong>`;

  // --- Cover -------------------------------------------------------------
  const cover =
    `<header style="background:${INK};color:#fff;padding:64px 64px 56px;">` +
    `<div style="height:18px;">${logoSvg}</div>` +
    `<p style="margin:36px 0 12px;${label(accent)}font-size:12px;">Contract · Services Agreement · ${esc(date)}</p>` +
    `<h1 style="margin:0 0 18px;font-size:38px;line-height:1.1;font-weight:800;letter-spacing:-0.02em;max-width:20ch;">${esc(contract.title)}</h1>` +
    `<p style="margin:0;font-size:16px;line-height:1.55;color:rgba(255,255,255,0.72);max-width:60ch;">By and between ${esc(brandName)} (&quot;Provider&quot;) and ${esc(clientName)} (&quot;Client&quot;).</p>` +
    `<div style="display:flex;flex-wrap:wrap;gap:36px;margin-top:40px;">` +
    `<div><div style="${label('rgba(255,255,255,0.4)')}">Provider</div><div style="margin-top:5px;font-size:14px;font-weight:600;">${esc(brandName)}</div></div>` +
    `<div><div style="${label('rgba(255,255,255,0.4)')}">Client</div><div style="margin-top:5px;font-size:14px;font-weight:600;">${esc(clientName)}</div></div>` +
    `<div><div style="${label('rgba(255,255,255,0.4)')}">Effective Date</div><div style="margin-top:5px;font-size:14px;font-weight:600;">${esc(date)}</div></div>` +
    (contract.validUntil
      ? `<div><div style="${label('rgba(255,255,255,0.4)')}">Execute by</div><div style="margin-top:5px;font-size:14px;font-weight:600;">${esc(fmtDate(contract.validUntil))}</div></div>`
      : '') +
    '</div>' +
    `<div style="display:inline-flex;align-items:baseline;gap:12px;margin-top:36px;padding:16px 24px;border:1px solid rgba(255,255,255,0.16);border-radius:14px;background:rgba(255,255,255,0.06);">` +
    `<span style="${label('rgba(255,255,255,0.5)')}">Contract value</span>` +
    `<span style="font-size:28px;font-weight:800;color:${accent};">${usd(totals.total)}</span>` +
    '</div>' +
    '</header>';

  // --- Parties -----------------------------------------------------------
  const partyCard = (
    role: string,
    name: string,
    rep: string,
    repTitle?: string,
  ): string =>
    `<div style="flex:1;min-width:240px;padding:16px 18px;border:1px solid ${LINE};border-radius:12px;background:${PAPER_2};">` +
    `<div style="${label(MUTED)}">${role}</div>` +
    `<div style="margin-top:5px;font-size:15px;font-weight:800;color:${INK};">${esc(name)}</div>` +
    `<div style="margin-top:3px;font-size:12.5px;color:${MUTED};">Represented by ${esc(rep)}${repTitle ? `, ${esc(repTitle)}` : ''}</div>` +
    '</div>';

  const parties =
    `<section style="margin:0 0 32px;">` +
    sectionTitle(accent, 'Parties') +
    p(
      `This Services Agreement (this ${defined('Agreement')}) is entered into as of ${esc(date)} (the ${defined('Effective Date')}) by and between ${esc(brandName)} (${defined('Provider')}) and ${esc(clientName)} (${defined('Client')}). Provider and Client are each a ${defined('Party')} and together the ${defined('Parties')}.`,
    ) +
    `<div style="display:flex;flex-wrap:wrap;gap:14px;margin-top:6px;">` +
    partyCard('Provider', brandName, owner) +
    partyCard('Client', clientName, pocName, pocTitle) +
    '</div>' +
    '</section>';

  // --- Recitals ----------------------------------------------------------
  const recitals =
    `<section style="margin:0 0 32px;">` +
    sectionTitle(accent, 'Recitals') +
    p(
      `WHEREAS, Provider is in the business of providing the professional services described in this Agreement;`,
    ) +
    p(
      `WHEREAS, Client desires to engage Provider to perform such services, and Provider is willing to perform them, on the terms and conditions set forth herein;`,
    ) +
    p(
      `NOW, THEREFORE, in consideration of the mutual covenants and agreements set forth below, and for other good and valuable consideration, the receipt and sufficiency of which are hereby acknowledged, the Parties agree as follows:`,
    ) +
    '</section>';

  // --- Operative clauses ---------------------------------------------------
  const scopeBody =
    items.length > 0
      ? p(
          `Provider shall perform, and Client hereby engages Provider to perform, the following services (collectively, the ${defined('Services')}):`,
        ) +
        `<ul style="margin:0 0 10px;padding-left:22px;max-width:74ch;">` +
        items
          .map(
            (item) =>
              `<li style="margin:0 0 8px;font-size:13.5px;line-height:1.65;color:${BODY_TEXT};"><strong style="color:${INK};">${esc(item.description)}</strong> — ${esc(String(item.qty))}${item.unit ? ` ${esc(item.unit)}` : ''}, ${item.cadence === 'recurring' ? 'recurring' : 'one-time'}.</li>`,
          )
          .join('') +
        '</ul>' +
        p(
          `Any material change to the Services shall be agreed in a written change order signed by both Parties before the changed work begins.`,
        )
      : p(
          `Provider shall perform the services (the ${defined('Services')}) described in the statement of work or proposal referenced by this Agreement. Any material change to the Services shall be agreed in a written change order signed by both Parties.`,
        );

  const feesBody =
    p(
      `In consideration of the Services, Client shall pay Provider the fees set forth below (the ${defined('Fees')}):`,
    ) +
    pricingRows(items, accent) +
    p(
      `One-time Fees are invoiced upon execution of this Agreement; recurring Fees are invoiced at the start of each billing period. Invoices are due net fifteen (15) days from the invoice date. Past-due amounts may accrue interest at the lesser of 1.5% per month or the maximum rate permitted by law. Fees are exclusive of applicable taxes, which are Client's responsibility (excluding taxes on Provider's income).`,
      'margin-top:12px;',
    );

  const termBody =
    p(
      `This Agreement commences on the Effective Date and continues until the Services are completed or the Agreement is terminated as provided herein.` +
        (contract.validUntil
          ? ` If not executed by both Parties on or before ${esc(fmtDate(contract.validUntil))}, this Agreement and its pricing lapse and must be re-issued.`
          : ''),
    ) +
    p(
      `Either Party may terminate this Agreement for material breach upon fifteen (15) days' written notice, provided the breach remains uncured at the end of the notice period. Upon termination, Client shall pay all Fees for Services performed through the effective date of termination. Sections 4 (Confidentiality), 5 (Intellectual Property), 7 (Limitation of Liability), and 9 (Governing Law) survive termination.`,
    );

  const confidentialityBody = p(
    `Each Party (the ${defined('Receiving Party')}) shall hold in strict confidence all non-public information disclosed by the other Party (the ${defined('Disclosing Party')}), shall use it solely to perform under this Agreement, and shall not disclose it to any third party except to personnel and advisors bound by obligations at least as protective. These obligations do not apply to information that is or becomes public through no fault of the Receiving Party, was lawfully known prior to disclosure, is independently developed, or must be disclosed by law (with prompt notice to the Disclosing Party where permitted).`,
  );

  const ipBody = p(
    `Upon full payment of the applicable Fees, Provider assigns to Client all right, title, and interest in the deliverables created specifically for Client under this Agreement. Provider retains all rights in its pre-existing materials, tools, know-how, and generalized skills, and grants Client a perpetual, non-exclusive license to use any such pre-existing materials solely as embedded in the deliverables.`,
  );

  const warrantiesBody = p(
    `Provider warrants that the Services will be performed in a professional and workmanlike manner consistent with industry standards. EXCEPT AS EXPRESSLY SET FORTH IN THIS AGREEMENT, THE SERVICES AND DELIVERABLES ARE PROVIDED &quot;AS IS&quot; AND EACH PARTY DISCLAIMS ALL OTHER WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE.`,
  );

  const liabilityBody = p(
    `EXCEPT FOR BREACHES OF SECTION 4 (CONFIDENTIALITY) OR A PARTY'S INDEMNIFICATION OBLIGATIONS, NEITHER PARTY SHALL BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES, AND EACH PARTY'S AGGREGATE LIABILITY UNDER THIS AGREEMENT SHALL NOT EXCEED THE TOTAL FEES PAID OR PAYABLE BY CLIENT IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM.`,
  );

  const relationshipBody = p(
    `Provider is an independent contractor. Nothing in this Agreement creates a partnership, joint venture, agency, or employment relationship between the Parties, and neither Party may bind the other.`,
  );

  const governingLawBody = p(
    `This Agreement is governed by the laws of the state of <span style="display:inline-block;min-width:140px;border-bottom:1px solid ${INK};">&nbsp;</span>, without regard to its conflict-of-laws rules. Any dispute arising out of or relating to this Agreement shall first be escalated to executives of each Party for good-faith resolution before either Party initiates formal proceedings.`,
  );

  const entireAgreementBody = p(
    `This Agreement constitutes the entire agreement between the Parties with respect to its subject matter and supersedes all prior or contemporaneous proposals, understandings, and communications, whether written or oral. Any amendment must be in a writing signed by both Parties. If any provision is held unenforceable, the remainder of this Agreement remains in full force. This Agreement may be executed in counterparts, including by electronic signature, each of which is deemed an original.`,
  );

  const clauses =
    clause(1, 'Scope of Services', scopeBody) +
    clause(2, 'Fees and Payment', feesBody) +
    clause(3, 'Term and Termination', termBody) +
    clause(4, 'Confidentiality', confidentialityBody) +
    clause(5, 'Intellectual Property', ipBody) +
    clause(6, 'Representations and Warranties', warrantiesBody) +
    clause(7, 'Limitation of Liability', liabilityBody) +
    clause(8, 'Relationship of the Parties', relationshipBody) +
    clause(9, 'Governing Law and Disputes', governingLawBody) +
    clause(10, 'Entire Agreement', entireAgreementBody);

  // --- Execution block -----------------------------------------------------
  const execution =
    `<section style="margin:0;padding:28px;border:1px solid ${LINE};border-radius:14px;background:${PAPER_2};page-break-inside:avoid;">` +
    sectionTitle(accent, 'Execution') +
    p(
      `IN WITNESS WHEREOF, the Parties have executed this Agreement by their duly authorized representatives as of the Effective Date.`,
    ) +
    `<div style="display:flex;flex-wrap:wrap;gap:40px;margin-top:8px;">` +
    signatureColumn(brandName, owner) +
    signatureColumn(clientName, pocName, pocTitle) +
    '</div>' +
    '</section>';

  const footer =
    `<footer style="background:${INK};color:rgba(255,255,255,0.6);padding:22px 64px;display:flex;align-items:center;justify-content:space-between;gap:20px;${label('rgba(255,255,255,0.6)')}">` +
    `<span style="height:14px;display:inline-flex;">${logoSvg}</span>` +
    `<span>${esc(brandName)} · ${esc(contract.title)}</span>` +
    '</footer>';

  return docShell({
    title: contract.title,
    accent,
    cover,
    main: parties + recitals + clauses + execution,
    footer,
  });
}
